import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import {
  AlertTriangle, AlertOctagon, CheckCircle, Info, ChevronLeft, ChevronRight, ClipboardList,
  ShieldCheck, Search, MapPin, Clock, X, BellRing,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";

const PAGE_SIZE = 25;
const SEVERITIES = ["critical", "high", "medium", "warning", "low", "info"];
const SEV = {
  critical: { stripe: "border-l-red-600", pill: "bg-red-50 text-red-700 border-red-200", icon: AlertOctagon, iconTone: "text-red-600" },
  high: { stripe: "border-l-orange-500", pill: "bg-orange-50 text-orange-700 border-orange-200", icon: AlertTriangle, iconTone: "text-orange-500" },
  medium: { stripe: "border-l-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle, iconTone: "text-amber-500" },
  warning: { stripe: "border-l-amber-500", pill: "bg-amber-50 text-amber-700 border-amber-200", icon: AlertTriangle, iconTone: "text-amber-500" },
  low: { stripe: "border-l-sky-500", pill: "bg-sky-50 text-sky-700 border-sky-200", icon: Info, iconTone: "text-sky-500" },
  info: { stripe: "border-l-slate-400", pill: "bg-slate-50 text-slate-600 border-slate-200", icon: Info, iconTone: "text-slate-400" },
};
const STATUS_PILL = {
  open: "bg-red-50 text-red-700 border-red-200",
  acknowledged: "bg-amber-50 text-amber-700 border-amber-200",
  resolved: "bg-emerald-50 text-emerald-700 border-emerald-200",
};
// Alert category → Work Order system, for "Raise WO".
const WO_SYSTEM = { hydrant: "Fire-Hydrant", sprinkler: "Sprinkler", tank: "Tank", detection: "Detection",
  ventilation: "Ventilation", pump: "Pumps", pumps: "Pumps", lift: "Lifts" };
const WO_PRIORITY = { critical: "Critical", high: "High", warning: "Medium", medium: "Medium", low: "Low", info: "Low" };

const statusOf = (a) => a.status || (a.acknowledged ? "acknowledged" : "open");

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (!Number.isFinite(s)) return "—";
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function Tile({ label, value, sub, tone, active, onClick, testid }) {
  const tones = {
    red: "text-red-700", amber: "text-amber-700", green: "text-emerald-700", slate: "text-gray-900",
  };
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`glass rounded-xl p-4 text-left transition border ${active ? "border-orange-400 ring-2 ring-orange-100" : "border-transparent hover:border-gray-300"}`}>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold">{label}</div>
      <div className={`text-3xl font-extrabold mt-1 tabular-nums ${tones[tone]}`}>{value ?? "—"}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </button>
  );
}

export default function AlertManagement() {
  const { sites, selectedSite, selectedOrg } = useSiteContext();
  const { user } = useAuth();
  const { can } = usePermissions();
  const nav = useNavigate();
  const isPlatform = user?.role === "super_admin" || user?.role === "platform_support";
  const canUpdate = can("alert_management", "update");

  const [scope, setScope] = useState("site"); // site | org
  const [statusTab, setStatusTab] = useState("open");
  const [sevSet, setSevSet] = useState(new Set());
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => { const t = setTimeout(() => { setSearch(query.trim()); setPage(1); }, 300); return () => clearTimeout(t); }, [query]);

  const scopeParams = useCallback(() => {
    const p = {};
    if (isPlatform && selectedOrg) p.org_id = selectedOrg;
    if (scope === "site" && selectedSite && selectedSite !== "all") p.site_id = selectedSite;
    return p;
  }, [isPlatform, selectedOrg, scope, selectedSite]);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    const params = { ...scopeParams(), limit: PAGE_SIZE + 1, skip: (page - 1) * PAGE_SIZE };
    if (statusTab !== "all") params.status = statusTab;
    if (sevSet.size) params.severity = [...sevSet].join(",");
    if (search) params.q = search;
    if (start) params.start = start;
    if (end) params.end = end;
    try {
      const [list, sum] = await Promise.all([
        api.get("/alerts", { params }),
        api.get("/alerts/summary", { params: scopeParams() }),
      ]);
      setRows(list.data.slice(0, PAGE_SIZE));
      setHasMore(list.data.length > PAGE_SIZE);
      setSummary(sum.data);
    } catch { if (!quiet) toast.error("Could not load alerts"); }
    finally { setLoading(false); }
  }, [scopeParams, page, statusTab, sevSet, search, start, end]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(() => load(true), 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { setPage(1); }, [selectedSite, selectedOrg, scope]);

  const act = async (a, action) => {
    setBusy(a.alert_id);
    try {
      await api.post(`/alerts/${a.alert_id}/${action}`);
      toast.success(action === "acknowledge" ? "Alert acknowledged" : "Alert resolved");
      load(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  const ackAllShown = async () => {
    const open = rows.filter((a) => statusOf(a) === "open");
    if (!open.length) return;
    if (!window.confirm(`Acknowledge the ${open.length} open alerts on this page? Each is logged under your name.`)) return;
    try {
      await Promise.all(open.map((a) => api.post(`/alerts/${a.alert_id}/acknowledge`)));
      toast.success(`${open.length} alerts acknowledged`);
      load(true);
    } catch { toast.error("Some alerts could not be acknowledged"); load(true); }
  };

  const raiseWO = (a) => {
    const params = new URLSearchParams({
      alert_id: a.alert_id, site_id: a.site_id || "",
      title: a.title || "Alert", desc: a.message || a.description || a.title || "",
      system: WO_SYSTEM[(a.category || "").toLowerCase()] || "Other",
      priority: WO_PRIORITY[a.severity] || "Medium",
    });
    nav(`/work-orders?${params.toString()}`);
  };

  const toggleSev = (s) => {
    const next = new Set(sevSet);
    next.has(s) ? next.delete(s) : next.add(s);
    setSevSet(next); setPage(1);
  };
  const filtersOn = sevSet.size || search || start || end;
  const clearFilters = () => { setSevSet(new Set()); setQuery(""); setSearch(""); setStart(""); setEnd(""); setPage(1); };

  const siteName = (id) => sites.find((s) => s.site_id === id)?.name || id;
  const scopeName = scope === "site" && selectedSite ? siteName(selectedSite) : "All sites";
  const openBySev = summary?.open_by_severity || {};
  const critical = openBySev.critical || 0;
  const tabs = [
    ["open", "Open", summary?.open],
    ["acknowledged", "Acknowledged", summary?.acknowledged],
    ["resolved", "Resolved", summary?.resolved],
    ["all", "All", summary?.total],
  ];

  return (
    <div className="space-y-5" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold">Alerts</h1>
          <p className="text-sm text-gray-500 mt-1 flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" strokeWidth={2.5} /> {scopeName} · refreshes every 15 s
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope}>
            <SelectTrigger className="w-40 bg-white border-gray-200 h-9 text-sm" data-testid="alert-scope"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white border-gray-200 text-gray-900">
              <SelectItem value="site">This site</SelectItem>
              <SelectItem value="org">All sites</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => nav("/alerts/settings")} className="h-9 border-gray-200 text-gray-700" data-testid="alerts-settings-link">
            <ShieldCheck className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Alert Settings
          </Button>
        </div>
      </div>

      {/* Summary tiles — click to filter */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile testid="tile-open" label="Open" value={summary?.open} tone={summary?.open ? "red" : "green"}
          sub={summary?.open ? "need attention" : "all clear"}
          active={statusTab === "open" && !sevSet.size} onClick={() => { setStatusTab("open"); setSevSet(new Set()); setPage(1); }} />
        <Tile testid="tile-critical" label="Critical open" value={critical} tone={critical ? "red" : "slate"}
          sub="highest severity"
          active={statusTab === "open" && sevSet.size === 1 && sevSet.has("critical")}
          onClick={() => { setStatusTab("open"); setSevSet(new Set(["critical"])); setPage(1); }} />
        <Tile testid="tile-acked" label="Acknowledged" value={summary?.acknowledged} tone="amber" sub="seen, not yet resolved"
          active={statusTab === "acknowledged"} onClick={() => { setStatusTab("acknowledged"); setPage(1); }} />
        <Tile testid="tile-24h" label="Last 24 hours" value={summary?.last_24h} tone="slate" sub="new alerts raised"
          active={false} onClick={() => {
            const d = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
            setStatusTab("all"); setStart(d); setEnd(""); setPage(1);
          }} />
      </div>

      {/* Toolbar */}
      <div className="glass rounded-xl p-3 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg bg-gray-100 p-1" role="tablist">
            {tabs.map(([v, label, n]) => (
              <button key={v} role="tab" aria-selected={statusTab === v} data-testid={`tab-${v}`}
                onClick={() => { setStatusTab(v); setPage(1); }}
                className={`px-3 py-1.5 rounded-md text-sm font-semibold transition ${statusTab === v ? "bg-white shadow-sm text-gray-900" : "text-gray-500 hover:text-gray-800"}`}>
                {label}{n != null && <span className="ml-1.5 text-xs text-gray-400 tabular-nums">{n.toLocaleString("en-IN")}</span>}
              </button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={2.5} />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search title or message"
              className="pl-9 bg-white border-gray-200 h-9" data-testid="alert-search" />
          </div>
          <div className="flex items-center gap-1.5">
            <Input type="date" value={start} onChange={(e) => { setStart(e.target.value); setPage(1); }} className="bg-white border-gray-200 h-9 w-[140px] text-sm" aria-label="From date" />
            <span className="text-gray-400 text-sm">–</span>
            <Input type="date" value={end} onChange={(e) => { setEnd(e.target.value); setPage(1); }} className="bg-white border-gray-200 h-9 w-[140px] text-sm" aria-label="To date" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500 mr-1">Severity</span>
          {SEVERITIES.map((s) => {
            const on = sevSet.has(s);
            return (
              <button key={s} onClick={() => toggleSev(s)} data-testid={`sev-chip-${s}`}
                className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize border transition ${on ? SEV[s].pill : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                {s}{openBySev[s] ? <span className="ml-1 opacity-70 tabular-nums">· {openBySev[s]} open</span> : null}
              </button>
            );
          })}
          {filtersOn ? (
            <button onClick={clearFilters} className="ml-auto text-xs text-gray-500 hover:text-gray-900 inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" strokeWidth={2.5} /> Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="alerts-list">
        {statusTab === "open" && canUpdate && rows.some((a) => statusOf(a) === "open") && (
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={ackAllShown} className="border-amber-200 text-amber-700 hover:bg-amber-50" data-testid="ack-all-btn">
              <CheckCircle className="w-4 h-4 mr-1.5" strokeWidth={2.5} /> Acknowledge all on this page
            </Button>
          </div>
        )}

        {loading && !rows.length && [0, 1, 2].map((i) => <div key={i} className="glass rounded-xl h-24 animate-pulse" />)}

        {rows.map((a) => {
          const sev = SEV[a.severity] || SEV.info;
          const SevIcon = sev.icon;
          const status = statusOf(a);
          return (
            <div key={a.alert_id} data-testid={`alert-row-${a.alert_id}`}
              className={`glass rounded-xl border-l-4 ${sev.stripe} p-4 flex flex-col md:flex-row md:items-center gap-3 ${status === "resolved" ? "opacity-75" : ""}`}>
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <SevIcon className={`w-5 h-5 mt-0.5 shrink-0 ${sev.iconTone}`} strokeWidth={2.5} />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-gray-900">{a.title}</span>
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${sev.pill}`}>{a.severity}</span>
                    <span className={`px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${STATUS_PILL[status]}`}>{status}</span>
                  </div>
                  {(a.message || a.description) && <div className="text-sm text-gray-600 mt-1">{a.message || a.description}</div>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-gray-500">
                    {scope === "org" && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" strokeWidth={2.5} />{siteName(a.site_id)}</span>}
                    <span className="inline-flex items-center gap-1" title={new Date(a.created_at).toLocaleString("en-IN")}>
                      <Clock className="w-3 h-3" strokeWidth={2.5} />{timeAgo(a.created_at)}
                    </span>
                    {a.value != null && a.parameter && <span>{a.parameter}: <b className="text-gray-700">{a.value}</b>{a.threshold != null && <> (limit {a.threshold})</>}</span>}
                    {status === "acknowledged" && a.acknowledged_by && <span>Acknowledged by {a.acknowledged_by}</span>}
                    {status === "resolved" && a.resolved_by && <span>Resolved by {a.resolved_by}</span>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 md:shrink-0 md:justify-end">
                {canUpdate && status === "open" && (
                  <Button size="sm" disabled={busy === a.alert_id} onClick={() => act(a, "acknowledge")}
                    className="h-8 bg-amber-100 hover:bg-amber-200 text-amber-800 border border-amber-200" data-testid={`ack-${a.alert_id}`}>Acknowledge</Button>
                )}
                {canUpdate && status !== "resolved" && (
                  <Button size="sm" disabled={busy === a.alert_id} onClick={() => act(a, "resolve")}
                    className="h-8 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-200" data-testid={`resolve-${a.alert_id}`}>Resolve</Button>
                )}
                {a.work_order_number ? (
                  <Button size="sm" variant="outline" onClick={() => nav(`/work-orders?wo=${a.work_order_id}`)}
                    className="h-8 border-gray-200 text-gray-700" data-testid={`wo-link-${a.alert_id}`}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />{a.work_order_number}
                  </Button>
                ) : status !== "resolved" && (
                  <Button size="sm" variant="outline" onClick={() => raiseWO(a)}
                    className="h-8 border-orange-200 text-orange-700 hover:bg-orange-50" data-testid={`raise-wo-${a.alert_id}`}>
                    <ClipboardList className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} />Raise WO
                  </Button>
                )}
              </div>
            </div>
          );
        })}

        {!loading && !rows.length && (
          <div className="glass rounded-xl p-10 text-center">
            {statusTab === "open" && !filtersOn ? (
              <><BellRing className="w-10 h-10 mx-auto mb-2 text-emerald-500" strokeWidth={2} />
                <div className="font-semibold text-gray-900">No open alerts</div>
                <div className="text-sm text-gray-500 mt-1">Everything at {scopeName} is running normally.</div></>
            ) : (
              <><CheckCircle className="w-10 h-10 mx-auto mb-2 text-gray-300" strokeWidth={2} />
                <div className="font-semibold text-gray-900">No alerts match these filters</div>
                {filtersOn ? <button onClick={clearFilters} className="text-sm text-orange-700 mt-1 hover:underline">Clear filters</button> : null}</>
            )}
          </div>
        )}
      </div>

      {(page > 1 || hasMore) && (
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)} data-testid="alerts-prev"><ChevronLeft className="w-4 h-4 mr-1" strokeWidth={2.5} />Newer</Button>
          <span className="text-xs text-gray-500">Page {page}</span>
          <Button variant="ghost" size="sm" disabled={!hasMore} onClick={() => setPage(page + 1)} data-testid="alerts-next">Older<ChevronRight className="w-4 h-4 ml-1" strokeWidth={2.5} /></Button>
        </div>
      )}
    </div>
  );
}
